// src/pages/Admin.js
// v6 – add admin selected-media hard purge UI
//
// Changes vs v5:
// ✅ Adds selected media purge tool for admin-only backend purge routes
// ✅ Supports preview + confirm flow for:
//      - POST /photos/admin/purge-media-preview
//      - POST /photos/admin/purge-media
// ✅ Allows comma/newline/space-separated media IDs
// ✅ Shows post-aware purge details before confirmation
// ✅ Keeps user deletion flow aligned with updated admin_management.py
//
// Notes:
// - This is a true destructive purge tool.
// - It is intentionally separate from moderation.
// - Backend remains source of truth for actual deletion behavior.

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import api from "../services/api";
import { API_PATHS } from "../config";
import "../styles/Admin.css";

const isTrialing = (u) =>
  u?.role === "PRINCIPAL" &&
  String(u?.subscription_status || "").toUpperCase() === "TRIALING" &&
  (typeof u?.trial_days_left === "number" ? u.trial_days_left > 0 : true);

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = (bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1);
  return `${val} ${units[i]}`;
};

const wpTechLabel = (val) => {
  if (!val) return "N/A";
  const s = String(val).toLowerCase();
  if (s.includes("wordpress") && !s.includes("non") && !s.includes("not")) {
    return "WordPress";
  }
  return "NON-WordPress";
};

const asArray = (v) => (Array.isArray(v) ? v : []);

const getDeletePreviewUrl = (email) => {
  if (typeof API_PATHS.DELETE_USER_PREVIEW === "function") {
    return API_PATHS.DELETE_USER_PREVIEW(email);
  }
  return `/auth/admin/delete-user-preview?email=${encodeURIComponent(email)}`;
};

const getDeleteConfirmUrl = (email) => {
  if (typeof API_PATHS.DELETE_USER_CONFIRM === "function") {
    return API_PATHS.DELETE_USER_CONFIRM(email);
  }
  return `/auth/users/${encodeURIComponent(email)}?confirm=true`;
};

const getPurgeMediaPreviewUrl = () => {
  if (typeof API_PATHS.PURGE_MEDIA_PREVIEW === "string") {
    return API_PATHS.PURGE_MEDIA_PREVIEW;
  }
  if (typeof API_PATHS.PURGE_MEDIA_PREVIEW === "function") {
    return API_PATHS.PURGE_MEDIA_PREVIEW();
  }
  return "/photos/admin/purge-media-preview";
};

const getPurgeMediaExecuteUrl = () => {
  if (typeof API_PATHS.PURGE_MEDIA === "string") {
    return API_PATHS.PURGE_MEDIA;
  }
  if (typeof API_PATHS.PURGE_MEDIA === "function") {
    return API_PATHS.PURGE_MEDIA();
  }
  return "/photos/admin/purge-media";
};

const getPreviewDbBlock = (payload) => {
  if (payload?.will_delete?.db && typeof payload.will_delete.db === "object") {
    return payload.will_delete.db;
  }
  return payload || {};
};

const getPreviewFilesBlock = (payload) => {
  if (payload?.will_delete?.files && typeof payload.will_delete.files === "object") {
    return payload.will_delete.files;
  }
  return payload || {};
};

const parseIdList = (raw) => {
  const parts = String(raw || "")
    .split(/[\s,]+/g)
    .map((s) => s.trim())
    .filter(Boolean);

  const nums = [];
  const seen = new Set();

  for (const part of parts) {
    const n = Number(part);
    if (!Number.isInteger(n) || n <= 0) continue;
    if (seen.has(n)) continue;
    seen.add(n);
    nums.push(n);
  }

  return nums;
};

const buildPurgePreviewConfirmMessage = (payload) => {
  const requestedCount = Number(payload?.requested_count || 0);
  const foundCount = Number(payload?.found_count || 0);
  const postsTouchedCount = Number(payload?.posts_touched_count || 0);
  const emptyPostsToDeleteCount = Number(payload?.empty_posts_to_delete_count || 0);
  const hashtagsCount = Number(payload?.photo_hashtags_count || 0);
  const classTagsCount = Number(payload?.photo_class_tags_count || 0);
  const likesCount = Number(payload?.photo_likes_count || 0);
  const commentsCount = Number(payload?.photo_comments_count || 0);
  const filesCount = Number(payload?.files_count || 0);
  const missingFilesCount = Number(payload?.missing_files_count || 0);
  const bytes = Number(payload?.bytes || 0);

  const sampleMedia = asArray(payload?.sample_media).slice(0, 10);
  const sampleEmptyPostIds = asArray(payload?.sample_empty_post_ids).slice(0, 10);
  const sampleFiles = asArray(payload?.sample_files).slice(0, 8);
  const sampleMissing = asArray(payload?.sample_missing).slice(0, 5);

  return `You are about to permanently purge selected media:

• Requested IDs: ${requestedCount}
• Found media rows: ${foundCount}
• Posts touched: ${postsTouchedCount}
• Empty posts to delete: ${emptyPostsToDeleteCount}
• Photo hashtags: ${hashtagsCount}
• Photo class tags: ${classTagsCount}
• Photo likes: ${likesCount}
• Photo comments: ${commentsCount}
• Files on disk: ${filesCount} (${formatBytes(bytes)})
${missingFilesCount ? `• Missing file references: ${missingFilesCount}` : ""}

${sampleMedia.length
    ? `Sample media:\n${sampleMedia
      .map(
        (m, i) =>
          `  ${i + 1}. photo_id=${m.photo_id} post_id=${m.post_id ?? "—"} school_id=${m.school_id ?? "—"} type=${m.media_type ?? "—"} status=${m.status ?? "—"}`
      )
      .join("\n")}`
    : ""}

${sampleEmptyPostIds.length
    ? `Sample empty post IDs to delete:\n${sampleEmptyPostIds
      .map((id, i) => `  ${i + 1}. ${id}`)
      .join("\n")}`
    : ""}

${sampleFiles.length
    ? `Sample existing files:\n${sampleFiles
      .map((s, i) => `  ${i + 1}. ${s}`)
      .join("\n")}`
    : ""}

${sampleMissing.length
    ? `Sample missing references:\n${sampleMissing
      .map((s, i) => `  ${i + 1}. ${s}`)
      .join("\n")}`
    : ""}

This action cannot be undone.

Proceed?`;
};

/**
 * Internal admin dashboard for managing users, principals, trials,
 * and destructive admin-only cleanup tools.
 *
 * @component
 * @returns {JSX.Element}
 */
const Admin = () => {
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pendingPrincipals, setPendingPrincipals] = useState([]);
  const [selectedPrincipals, setSelectedPrincipals] = useState([]);

  const [tableLoading, setTableLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [doingBulk, setDoingBulk] = useState(false);
  const [error, setError] = useState("");

  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState("STAFF");
  const [extraDaysMap, setExtraDaysMap] = useState({});

  const [purgeMediaInput, setPurgeMediaInput] = useState("");
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeBusy, setPurgeBusy] = useState(false);

  const [banner, setBanner] = useState(null);
  const bannerTimerRef = useRef(null);

  const [rowBusy, setRowBusy] = useState({});

  const navigate = useNavigate();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    };
  }, []);

  const showBanner = (text, type = "success", ms = 4000) => {
    setBanner({ text, type });
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setBanner(null), ms);
  };

  const setBusy = (key, val) =>
    setRowBusy((prev) => ({ ...prev, [key]: val }));

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    const load = async () => {
      setTableLoading(true);
      setError("");

      try {
        const [usersRes, pendingRes] = await Promise.all([
          api.get(API_PATHS.ADMIN_USERS),
          api.get(API_PATHS.PENDING_PRINCIPALS),
        ]);

        if (!mountedRef.current) return;

        const u = Array.isArray(usersRes.data) ? usersRes.data : [];
        setUsers(u);
        setTotalUsers(u.length);
        setPendingPrincipals(Array.isArray(pendingRes.data) ? pendingRes.data : []);

        const nextMap = {};
        u.forEach((x) => {
          if (x.role === "PRINCIPAL") nextMap[x.email] = 7;
        });
        setExtraDaysMap(nextMap);
      } catch (err) {
        if (!mountedRef.current) return;
        setError("Failed to load admin data.");
        console.error(err);
      } finally {
        if (mountedRef.current) setTableLoading(false);
      }
    };

    load();
  }, [navigate]);

  const refreshAdminData = async () => {
    try {
      const [usersRes, pendingRes] = await Promise.all([
        api.get(API_PATHS.ADMIN_USERS),
        api.get(API_PATHS.PENDING_PRINCIPALS),
      ]);

      const u = Array.isArray(usersRes.data) ? usersRes.data : [];
      setUsers(u);
      setTotalUsers(u.length);
      setPendingPrincipals(Array.isArray(pendingRes.data) ? pendingRes.data : []);

      const nextMap = {};
      u.forEach((x) => {
        if (x.role === "PRINCIPAL") nextMap[x.email] = 7;
      });
      setExtraDaysMap(nextMap);
    } catch {
      // Silent — data might be slightly stale
    }
  };

  const handleSelectPrincipal = (email) => {
    setSelectedPrincipals((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  const handleApprovePrincipal = async (email) => {
    const key = `approve:${email}`;
    setBusy(key, true);
    try {
      const { data } = await api.post(API_PATHS.APPROVE_PRINCIPAL(email));
      showBanner(data?.message || "Principal approved.", "success");
      await refreshAdminData();
    } catch (err) {
      showBanner(
        "Failed to approve principal: " +
          (err?.response?.data?.detail || err?.message),
        "error"
      );
    } finally {
      setBusy(key, false);
    }
  };

  const handleRejectPrincipal = async (email) => {
    if (!window.confirm(`Reject ${email}?`)) return;
    const key = `reject:${email}`;
    setBusy(key, true);
    try {
      const { data } = await api.post(API_PATHS.REJECT_PRINCIPAL(email));
      showBanner(data?.message || "Principal rejected.", "success");
      await refreshAdminData();
    } catch (err) {
      showBanner(
        "Failed to reject principal: " +
          (err?.response?.data?.detail || err?.message),
        "error"
      );
    } finally {
      setBusy(key, false);
    }
  };

  const handleApprovePrincipals = async () => {
    if (selectedPrincipals.length === 0) return;
    setDoingBulk(true);
    try {
      const results = await Promise.allSettled(
        selectedPrincipals.map((email) => api.post(API_PATHS.APPROVE_PRINCIPAL(email)))
      );

      const successes = results.filter((r) => r.status === "fulfilled").length;
      const failures = results
        .map((r, i) => (r.status === "rejected" ? selectedPrincipals[i] : null))
        .filter(Boolean);

      showBanner(
        `Approved: ${successes}${failures.length ? ` — Failed: ${failures.join(", ")}` : ""}`,
        failures.length ? "info" : "success"
      );
      setSelectedPrincipals([]);
      await refreshAdminData();
    } catch {
      showBanner("Bulk approval failed.", "error");
    } finally {
      setDoingBulk(false);
    }
  };

  const handleRejectPrincipals = async () => {
    if (selectedPrincipals.length === 0) return;
    if (!window.confirm("Reject all selected principals?")) return;

    setDoingBulk(true);
    try {
      const results = await Promise.allSettled(
        selectedPrincipals.map((email) => api.post(API_PATHS.REJECT_PRINCIPAL(email)))
      );

      const successes = results.filter((r) => r.status === "fulfilled").length;
      const failures = results
        .map((r, i) => (r.status === "rejected" ? selectedPrincipals[i] : null))
        .filter(Boolean);

      showBanner(
        `Rejected: ${successes}${failures.length ? ` — Failed: ${failures.join(", ")}` : ""}`,
        failures.length ? "info" : "success"
      );
      setSelectedPrincipals([]);
      await refreshAdminData();
    } catch {
      showBanner("Bulk rejection failed.", "error");
    } finally {
      setDoingBulk(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail.trim()) return;

    setActionLoading(true);
    try {
      const { data } = await api.post(API_PATHS.CREATE_USER, {
        email: newUserEmail.trim(),
        role: newUserRole,
      });
      showBanner(data?.message || "User created.", "success");
      setNewUserEmail("");
      await refreshAdminData();
    } catch (err) {
      showBanner(
        "Failed to create user: " +
          (err?.response?.data?.detail || err?.message),
        "error"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (email) => {
    const key = `delete:${email}`;

    try {
      const previewRes = await api.get(getDeletePreviewUrl(email));
      const payload = previewRes?.data || {};

      const dbInfo = getPreviewDbBlock(payload);
      const filesInfo = getPreviewFilesBlock(payload);

      const photosCount = dbInfo?.photos_count ?? payload?.photos_count ?? 0;
      const postsTouchedCount = dbInfo?.posts_touched_count ?? payload?.posts_touched_count ?? 0;
      const emptyPostsToDeleteCount = dbInfo?.empty_posts_to_delete_count ?? payload?.empty_posts_to_delete_count ?? 0;
      const hashtagsCount = dbInfo?.photo_hashtags_count ?? payload?.photo_hashtags_count ?? 0;
      const classTagsCount = dbInfo?.photo_class_tags_count ?? payload?.photo_class_tags_count ?? 0;
      const likesCount = dbInfo?.photo_likes_count ?? payload?.photo_likes_count ?? 0;
      const commentsCount = dbInfo?.photo_comments_count ?? payload?.photo_comments_count ?? 0;
      const resetCount = dbInfo?.reset_codes_count ?? payload?.reset_count ?? 0;
      const apiKeysCount = dbInfo?.api_keys_for_school ?? payload?.api_keys_for_school ?? 0;
      const parentInvitesCount = dbInfo?.parent_invites_for_school ?? payload?.parent_invites_for_school ?? 0;
      const willResetSchoolHashtag = Boolean(
        dbInfo?.will_reset_school_hashtag ?? payload?.will_reset_school_hashtag
      );

      const filesCount = filesInfo?.count ?? payload?.files_count ?? 0;
      const missingCount = filesInfo?.missing_count ?? payload?.missing_files_count ?? 0;
      const bytes = filesInfo?.total_size_bytes ?? payload?.bytes ?? 0;

      const sampleExisting =
        asArray(filesInfo?.sample_existing).length > 0
          ? asArray(filesInfo.sample_existing).slice(0, 5)
          : asArray(payload?.sample_files).slice(0, 5);

      const sampleMissing =
        asArray(filesInfo?.sample_missing).length > 0
          ? asArray(filesInfo.sample_missing).slice(0, 3)
          : asArray(payload?.sample_missing).slice(0, 3);

      const sampleEmptyPosts =
        asArray(dbInfo?.sample_empty_post_ids).length > 0
          ? asArray(dbInfo.sample_empty_post_ids).slice(0, 10)
          : asArray(payload?.sample_empty_post_ids).slice(0, 10);

      const confirmMsg = `You are about to permanently delete:

• User: ${email}
• Media rows (photos/videos): ${photosCount}
• Posts touched: ${postsTouchedCount}
• Empty posts to delete: ${emptyPostsToDeleteCount}
• Photo hashtags: ${hashtagsCount}
• Photo class tags: ${classTagsCount}
• Photo likes: ${likesCount}
• Photo comments: ${commentsCount}
• Reset codes: ${resetCount}
• Files on disk: ${filesCount} (${formatBytes(bytes)})
${missingCount ? `• Missing file references: ${missingCount}` : ""}
${apiKeysCount ? `• School API keys to delete: ${apiKeysCount}` : ""}
${parentInvitesCount ? `• Parent invites to delete: ${parentInvitesCount}` : ""}
${willResetSchoolHashtag ? "• School hashtag will be cleared" : ""}

${sampleEmptyPosts.length
    ? `Sample empty post IDs to delete:\n${sampleEmptyPosts
      .map((id, i) => `  ${i + 1}. ${id}`)
      .join("\n")}`
    : ""}

${sampleExisting.length
    ? `Sample existing files:\n${sampleExisting
      .map((s, i) => `  ${i + 1}. ${s}`)
      .join("\n")}`
    : ""}

${sampleMissing.length
    ? `Sample missing references:\n${sampleMissing
      .map((s, i) => `  ${i + 1}. ${s}`)
      .join("\n")}`
    : ""}

This action cannot be undone.

Proceed?`;

      if (!window.confirm(confirmMsg)) return;

      setBusy(key, true);
      await api.delete(getDeleteConfirmUrl(email));
      showBanner(`User ${email} and related data deleted.`, "success");
      await refreshAdminData();
    } catch (err) {
      showBanner(
        "Failed to delete user: " +
          (err?.response?.data?.detail || err?.message),
        "error"
      );
    } finally {
      setBusy(key, false);
    }
  };

  const handlePreviewPurgeMedia = async () => {
    const ids = parseIdList(purgeMediaInput);

    if (ids.length === 0) {
      showBanner("Enter at least one valid media ID.", "error");
      return;
    }

    setPurgeBusy(true);
    try {
      const { data } = await api.post(getPurgeMediaPreviewUrl(), {
        photo_ids: ids,
      });

      setPurgePreview(data || null);
      showBanner(`Preview ready for ${ids.length} selected media ID(s).`, "success");
    } catch (err) {
      setPurgePreview(null);
      showBanner(
        "Failed to preview selected media purge: " +
          (err?.response?.data?.detail?.message ||
            err?.response?.data?.detail ||
            err?.message),
        "error"
      );
    } finally {
      setPurgeBusy(false);
    }
  };

  const handleExecutePurgeMedia = async () => {
    const ids = parseIdList(purgeMediaInput);

    if (ids.length === 0) {
      showBanner("Enter at least one valid media ID.", "error");
      return;
    }

    let previewPayload = purgePreview;

    try {
      if (!previewPayload) {
        const previewRes = await api.post(getPurgeMediaPreviewUrl(), {
          photo_ids: ids,
        });
        previewPayload = previewRes?.data || null;
        setPurgePreview(previewPayload);
      }

      const confirmMsg = buildPurgePreviewConfirmMessage(previewPayload);
      if (!window.confirm(confirmMsg)) return;

      setPurgeBusy(true);

      const { data } = await api.post(getPurgeMediaExecuteUrl(), {
        photo_ids: ids,
      });

      const deleted = data?.deleted || {};
      showBanner(
        `Purged ${deleted.photos_rows ?? ids.length} media row(s), deleted ${deleted.posts_deleted ?? 0} empty post(s), removed ${deleted.files_deleted ?? 0} file(s).`,
        "success",
        6000
      );

      setPurgeMediaInput("");
      setPurgePreview(null);
      await refreshAdminData();
    } catch (err) {
      showBanner(
        "Failed to purge selected media: " +
          (err?.response?.data?.detail?.message ||
            err?.response?.data?.detail ||
            err?.message),
        "error"
      );
    } finally {
      setPurgeBusy(false);
    }
  };

  const handleExtendTrial = async (email) => {
    const days = Number(extraDaysMap[email] ?? 7);
    if (!Number.isFinite(days) || days < 1 || days > 30) {
      showBanner("Days must be 1–30.", "error");
      return;
    }

    const key = `extend:${email}`;
    setBusy(key, true);
    try {
      const { data } = await api.post(API_PATHS.EXTEND_TRIAL(email, days));
      showBanner(data?.message || `Trial extended by ${days} day(s).`, "success");
      await refreshAdminData();
    } catch (err) {
      showBanner(
        "Failed to extend trial: " +
          (err?.response?.data?.detail || err?.message),
        "error"
      );
    } finally {
      setBusy(key, false);
    }
  };

  const handleWipeTrialSchool = async (email) => {
    const key = `wipe:${email}`;
    const confirmMsg =
      "⚠️ HARD WIPE TRIAL SCHOOL ⚠️\n\n" +
      "This will permanently delete:\n" +
      "• Principal account\n" +
      "• All staff & parent accounts\n" +
      "• All photos & files\n" +
      "• Invites and API keys\n" +
      "• Stripe customer (if any)\n\n" +
      "School record remains, but all user data is gone.\n" +
      "Use only for cleaning test/trial schools.\n\n" +
      "This cannot be undone.\n\n" +
      `Wipe trial data for ${email}?`;

    if (!window.confirm(confirmMsg)) return;

    setBusy(key, true);
    try {
      const { data } = await api.post(API_PATHS.WIPE_TRIAL_SCHOOL, {
        principal_email: email,
      });
      showBanner(data?.message || `Trial data wiped for ${email}.`, "success", 6000);
      await refreshAdminData();
    } catch (err) {
      showBanner(
        "Failed to wipe trial school: " +
          (err?.response?.data?.detail || err?.message),
        "error"
      );
    } finally {
      setBusy(key, false);
    }
  };

  const readout = useMemo(() => {
    const principals = users.filter((u) => u.role === "PRINCIPAL");
    const byStatus = principals.reduce(
      (acc, u) => {
        const s = String(u.subscription_status || "UNKNOWN").toUpperCase();
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      },
      { TRIALING: 0, ACTIVE: 0, INACTIVE: 0, UNKNOWN: 0 }
    );

    const expiringSoon = principals.filter(
      (u) =>
        String(u.subscription_status || "").toUpperCase() === "TRIALING" &&
        typeof u.trial_days_left === "number" &&
        u.trial_days_left >= 0 &&
        u.trial_days_left <= 2
    );

    return { principalsCount: principals.length, byStatus, expiringSoon };
  }, [users]);

  if (tableLoading) {
    return (
      <div className="loading">
        <p>Loading admin data…</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="dashboard-container">
        <header className="admin-header">
          <div>
            <p className="admin-kicker">Internal tools</p>
            <h1 className="admin-title">Admin Dashboard</h1>
            <p className="admin-subtitle">
              Approve principals, manage users, monitor trials, and run destructive cleanup tools.
            </p>
          </div>
        </header>

        {error && (
          <div className="admin-error-inline" role="alert">
            {error}
          </div>
        )}

        {banner && (
          <div className={`admin-banner admin-banner--${banner.type}`} role="status">
            {banner.text}
          </div>
        )}

        <section className="admin-section">
          <h2 className="admin-section-title">Overview</h2>
          <div className="admin-metrics-row">
            <div className="cardish">
              <strong>Total Users:</strong> {totalUsers}
            </div>
            <div className="cardish">
              <strong>Principals:</strong> {readout.principalsCount}
            </div>
            <div className="cardish">
              <strong>Trials:</strong> {readout.byStatus.TRIALING}
            </div>
            <div className="cardish">
              <strong>Active:</strong> {readout.byStatus.ACTIVE}
            </div>
            <div className="cardish">
              <strong>Inactive:</strong> {readout.byStatus.INACTIVE}
            </div>
            <div className="cardish">
              <strong>Expiring ≤2 days:</strong> {readout.expiringSoon.length}
            </div>
          </div>

          {readout.expiringSoon.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary>Show expiring trials</summary>
              <ul style={{ marginTop: 8 }}>
                {readout.expiringSoon.map((u) => (
                  <li key={u.email}>
                    {u.email} — {u.trial_days_left} day(s) left
                    {u.trial_ends_at_iso
                      ? ` (ends ${new Date(u.trial_ends_at_iso).toLocaleString()})`
                      : ""}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>

        <section className="admin-section">
          <h2 className="admin-section-title">Admin Media Purge</h2>
          <p style={{ marginBottom: 10 }}>
            Permanently delete selected media rows, related child rows, storage files, and any posts left empty afterward.
          </p>

          <div className="admin-create-user-form">
            <div className="admin-create-row" style={{ alignItems: "stretch", flexDirection: "column", gap: 10 }}>
              <textarea
                value={purgeMediaInput}
                onChange={(e) => setPurgeMediaInput(e.target.value)}
                placeholder="Paste media IDs here, e.g. 101, 102, 103"
                className="admin-input"
                rows={5}
                style={{ minHeight: 110, resize: "vertical" }}
              />

              <div className="admin-actions">
                <button
                  type="button"
                  className="admin-btn"
                  onClick={handlePreviewPurgeMedia}
                  disabled={purgeBusy}
                >
                  {purgeBusy ? "Previewing…" : "Preview Purge"}
                </button>

                <button
                  type="button"
                  className="admin-btn admin-btn--danger"
                  onClick={handleExecutePurgeMedia}
                  disabled={purgeBusy}
                >
                  {purgeBusy ? "Purging…" : "Purge Selected Media"}
                </button>
              </div>
            </div>
          </div>

          {purgePreview && (
            <details style={{ marginTop: 12 }}>
              <summary>Show latest purge preview</summary>
              <div style={{ marginTop: 10 }}>
                <p><strong>Requested:</strong> {purgePreview.requested_count ?? 0}</p>
                <p><strong>Found:</strong> {purgePreview.found_count ?? 0}</p>
                <p><strong>Posts touched:</strong> {purgePreview.posts_touched_count ?? 0}</p>
                <p><strong>Empty posts to delete:</strong> {purgePreview.empty_posts_to_delete_count ?? 0}</p>
                <p><strong>Hashtags:</strong> {purgePreview.photo_hashtags_count ?? 0}</p>
                <p><strong>Class tags:</strong> {purgePreview.photo_class_tags_count ?? 0}</p>
                <p><strong>Likes:</strong> {purgePreview.photo_likes_count ?? 0}</p>
                <p><strong>Comments:</strong> {purgePreview.photo_comments_count ?? 0}</p>
                <p><strong>Files:</strong> {purgePreview.files_count ?? 0}</p>
                <p><strong>Missing file refs:</strong> {purgePreview.missing_files_count ?? 0}</p>
                <p><strong>Bytes:</strong> {formatBytes(Number(purgePreview.bytes || 0))}</p>

                {asArray(purgePreview.sample_media).length > 0 && (
                  <>
                    <h4>Sample media</h4>
                    <pre style={{ whiteSpace: "pre-wrap" }}>
                      {JSON.stringify(asArray(purgePreview.sample_media), null, 2)}
                    </pre>
                  </>
                )}

                {asArray(purgePreview.sample_empty_post_ids).length > 0 && (
                  <>
                    <h4>Sample empty post IDs</h4>
                    <pre style={{ whiteSpace: "pre-wrap" }}>
                      {JSON.stringify(asArray(purgePreview.sample_empty_post_ids), null, 2)}
                    </pre>
                  </>
                )}
              </div>
            </details>
          )}
        </section>

        <section className="admin-section">
          <h2 className="admin-section-title">Create New User</h2>
          <form onSubmit={handleCreateUser} className="admin-create-user-form">
            <div className="admin-create-row">
              <input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="Enter user email"
                required
                className="admin-input"
              />
              <select
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value)}
                className="admin-select"
              >
                <option value="ADMIN">Admin</option>
                <option value="PRINCIPAL">Principal</option>
                <option value="STAFF">Staff</option>
                <option value="PARENT">Parent</option>
              </select>
              <button
                type="submit"
                disabled={actionLoading}
                className="admin-btn admin-btn--primary"
              >
                {actionLoading ? "Creating…" : "Create User"}
              </button>
            </div>
          </form>
        </section>

        <section className="admin-section">
          <h2 className="admin-section-title">All Users</h2>
          {users.length > 0 ? (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Photos</th>
                    <th>Status</th>
                    <th>Display Name</th>
                    <th>School</th>
                    <th>Website</th>
                    <th>Tech</th>
                    <th>Sub</th>
                    <th>Trial Days</th>
                    <th>Trial Ends</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const delKey = `delete:${user.email}`;
                    const extKey = `extend:${user.email}`;
                    const wipeKey = `wipe:${user.email}`;
                    const isPrincipal = user.role === "PRINCIPAL";
                    const canWipeTrial = isTrialing(user);

                    return (
                      <tr key={user.email}>
                        <td className="cell-ellipsis">{user.email}</td>
                        <td>{user.role}</td>
                        <td style={{ textAlign: "right" }}>{user.total_photos ?? 0}</td>
                        <td>
                          <span
                            className={`chip ${
                              user.status === "ACTIVE"
                                ? "chip--ok"
                                : user.status === "PENDING"
                                  ? "chip--warn"
                                  : "chip--muted"
                            }`}
                          >
                            {user.status || "—"}
                          </span>
                        </td>
                        <td className="cell-ellipsis">{user.display_name || "N/A"}</td>
                        <td className="cell-ellipsis">{user.school_name || "N/A"}</td>
                        <td className="cell-ellipsis">
                          {user.school_website ? (
                            <a href={user.school_website} target="_blank" rel="noopener noreferrer">
                              {user.school_website}
                            </a>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>{wpTechLabel(user.website_tech_used)}</td>
                        <td style={{ textAlign: "center" }}>
                          {isPrincipal ? String(user.subscription_status || "—").toUpperCase() : "—"}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {isPrincipal && typeof user.trial_days_left === "number"
                            ? user.trial_days_left
                            : "—"}
                        </td>
                        <td>
                          {isPrincipal && user.trial_ends_at_iso
                            ? new Date(user.trial_ends_at_iso).toLocaleString()
                            : "—"}
                        </td>
                        <td>
                          <div className="admin-actions">
                            <button
                              className="admin-btn admin-btn--danger"
                              onClick={() => handleDeleteUser(user.email)}
                              disabled={rowBusy[delKey]}
                            >
                              {rowBusy[delKey] ? "Deleting…" : "Delete"}
                            </button>

                            {isTrialing(user) && (
                              <>
                                <input
                                  type="number"
                                  min={1}
                                  max={30}
                                  value={extraDaysMap[user.email] ?? 7}
                                  onChange={(e) =>
                                    setExtraDaysMap((prev) => ({
                                      ...prev,
                                      [user.email]: Number(e.target.value),
                                    }))
                                  }
                                  className="admin-trial-input"
                                  title="Extra days"
                                  disabled={rowBusy[extKey]}
                                />
                                <button
                                  className="admin-btn"
                                  onClick={() => handleExtendTrial(user.email)}
                                  disabled={rowBusy[extKey]}
                                >
                                  {rowBusy[extKey] ? "Extending…" : "Extend Trial"}
                                </button>
                              </>
                            )}

                            {canWipeTrial && (
                              <button
                                className="admin-btn admin-btn--danger"
                                onClick={() => handleWipeTrialSchool(user.email)}
                                disabled={rowBusy[wipeKey]}
                                title="Hard delete trial school data"
                              >
                                {rowBusy[wipeKey] ? "Wiping…" : "Wipe Trial School"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No users found.</p>
          )}
        </section>

        <section className="admin-section">
          <h2 className="admin-section-title">Pending Principals</h2>
          {pendingPrincipals.length > 0 ? (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Email</th>
                      <th>School</th>
                      <th>Website</th>
                      <th>Tech</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingPrincipals.map((p) => {
                      const approveKey = `approve:${p.email}`;
                      const rejectKey = `reject:${p.email}`;
                      return (
                        <tr key={p.email}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedPrincipals.includes(p.email)}
                              onChange={() => handleSelectPrincipal(p.email)}
                            />
                          </td>
                          <td className="cell-ellipsis">{p.email}</td>
                          <td className="cell-ellipsis">{p.school_name}</td>
                          <td className="cell-ellipsis">
                            {p.school_website ? (
                              <a href={p.school_website} target="_blank" rel="noopener noreferrer">
                                {p.school_website}
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </td>
                          <td>{wpTechLabel(p.website_tech_used)}</td>
                          <td>{new Date(p.created_at).toLocaleString()}</td>
                          <td>
                            <div className="admin-actions">
                              <button
                                onClick={() => handleApprovePrincipal(p.email)}
                                disabled={doingBulk || rowBusy[approveKey]}
                                className="admin-btn admin-btn--primary"
                              >
                                {rowBusy[approveKey] ? "Approving…" : "Approve"}
                              </button>
                              <button
                                onClick={() => handleRejectPrincipal(p.email)}
                                disabled={doingBulk || rowBusy[rejectKey]}
                                className="admin-btn admin-btn--danger"
                              >
                                {rowBusy[rejectKey] ? "Rejecting…" : "Reject"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="admin-bulk-actions">
                <button
                  onClick={handleApprovePrincipals}
                  disabled={selectedPrincipals.length === 0 || doingBulk}
                  className="admin-btn admin-btn--primary"
                >
                  {doingBulk ? "Approving…" : "Approve Selected"}
                </button>
                <button
                  onClick={handleRejectPrincipals}
                  disabled={selectedPrincipals.length === 0 || doingBulk}
                  className="admin-btn admin-btn--danger"
                >
                  {doingBulk ? "Rejecting…" : "Reject Selected"}
                </button>
              </div>
            </>
          ) : (
            <p>No pending principals.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default Admin;